import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Wrench, Trees, Trash2, Building2, Info, Container } from "lucide-react";

const CAT_ICON = {
  post_construction: Wrench,
  land_clearing: Trees,
  rubbish_removal: Trash2,
  commercial_cleaning: Building2,
};

const AUD = (n) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(Math.round(n));

/**
 * EstimateWidget — live indicative-pricing calculator.
 * Two modes: "cleanup" (m²/m³-based ranges) and "skip_bin" (fixed size + waste type).
 * Emits state changes to parent via onChange.
 */
export default function EstimateWidget({
  category,
  size,
  onChange,
  service_kind,       // "cleanup" | "skip_bin"
  skip_bin_size,      // e.g. "6m3"
  skip_bin_waste,     // e.g. "general"
}) {
  const [rates, setRates] = useState(null);
  const [skipBins, setSkipBins] = useState(null);
  const [loadErr, setLoadErr] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/pricing-rates").then((r) => setRates(r.data)),
      api.get("/skip-bin-rates").then((r) => setSkipBins(r.data)),
    ]).catch(() => setLoadErr("Couldn't load rates — you can still submit the form."));
  }, []);

  const kind = service_kind || "cleanup";
  const setKind = (v) => onChange && onChange({ service_kind: v });
  const setSkipSize = (v) => onChange && onChange({ skip_bin_size: v });
  const setSkipWaste = (v) => onChange && onChange({ skip_bin_waste: v });

  // ------- CLEANUP MODE --------
  const numeric = Number(size);
  const isValidSize = size !== "" && !Number.isNaN(numeric) && numeric > 0;
  const range = useMemo(() => {
    if (!rates || !category || !rates[category] || !isValidSize) return null;
    const r = rates[category];
    return { min: numeric * r.min, max: numeric * r.max, rate: r };
  }, [rates, category, numeric, isValidSize]);

  // ------- SKIP BIN MODE -------
  const selectedSize = useMemo(
    () => skipBins?.sizes?.find((s) => s.code === skip_bin_size) || null,
    [skipBins, skip_bin_size]
  );
  const selectedWaste = useMemo(
    () => skipBins?.waste_types?.find((w) => w.code === (skip_bin_waste || "general")) || null,
    [skipBins, skip_bin_waste]
  );
  const skipMin = selectedSize ? Number(selectedSize.min) : null;
  const skipMax = selectedSize ? Number(selectedSize.max) : null;

  // Bubble up estimates so the lead payload carries them
  useEffect(() => {
    if (!onChange) return;
    if (kind === "cleanup") {
      onChange({
        estimate_min: range ? Number(range.min.toFixed(2)) : null,
        estimate_max: range ? Number(range.max.toFixed(2)) : null,
      });
    } else {
      onChange({
        estimate_min: skipMin,
        estimate_max: skipMax,
      });
    }
    // eslint-disable-next-line
  }, [kind, range?.min, range?.max, skipMin, skipMax]);

  const setCategory = (c) => onChange && onChange({ service_category: c });
  const setSize = (v) => onChange && onChange({ size: v });

  if (loadErr) return <p className="text-xs text-muted-foreground">{loadErr}</p>;
  if (!rates || !skipBins) return <p className="text-xs text-muted-foreground">Loading indicative rates…</p>;

  const selected = category && rates[category];

  return (
    <div className="space-y-5" data-testid="estimate-widget">
      {/* SERVICE KIND TABS */}
      <div className="grid grid-cols-2 gap-0 border border-border" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={kind === "cleanup"}
          onClick={() => setKind("cleanup")}
          data-testid="estimate-tab-cleanup"
          className={`p-3 text-sm uppercase font-display tracking-widest border-r border-border transition-colors ${
            kind === "cleanup" ? "bg-asphalt text-white" : "bg-white text-asphalt hover:bg-concrete"
          }`}
        >
          Cleanup service
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={kind === "skip_bin"}
          onClick={() => setKind("skip_bin")}
          data-testid="estimate-tab-skipbin"
          className={`p-3 text-sm uppercase font-display tracking-widest transition-colors flex items-center justify-center gap-2 ${
            kind === "skip_bin" ? "bg-asphalt text-white" : "bg-white text-asphalt hover:bg-concrete"
          }`}
        >
          <Container size={14} /> Skip bin hire
        </button>
      </div>

      {kind === "cleanup" && (
        <>
          <div>
            <span className="rs-label">Service category</span>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.entries(rates).map(([slug, r]) => {
                const Icon = CAT_ICON[slug] || Wrench;
                const active = slug === category;
                return (
                  <button
                    key={slug}
                    type="button"
                    onClick={() => setCategory(slug)}
                    data-testid={`estimate-cat-${slug}`}
                    className={`text-left p-3 border transition-colors flex items-start gap-3 ${
                      active ? "border-safety bg-safety/5" : "border-border bg-white hover:border-asphalt"
                    }`}
                  >
                    <span className={`w-8 h-8 grid place-items-center shrink-0 ${active ? "bg-safety text-white" : "bg-muted text-asphalt"}`}>
                      <Icon size={15} />
                    </span>
                    <span className="text-sm">
                      <span className="font-display uppercase text-[13px] leading-tight block">{r.label}</span>
                      <span className="text-[11px] text-muted-foreground">
                        {AUD(r.min)}–{AUD(r.max)} / {r.unit}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {selected && (
            <div>
              <label className="block">
                <span className="rs-label">
                  Approximate size <span className="text-muted-foreground normal-case tracking-normal font-normal">({selected.unit})</span>
                </span>
                <input
                  data-testid="estimate-size"
                  className="rs-input"
                  type="number"
                  min="0"
                  step="0.1"
                  inputMode="decimal"
                  placeholder={`e.g. 200 ${selected.unit}`}
                  value={size ?? ""}
                  onChange={(e) => setSize(e.target.value)}
                />
              </label>

              {range ? (
                <div className="mt-4 border border-border bg-white" data-testid="estimate-result">
                  <div className="p-4 bg-asphalt text-white">
                    <p className="rs-eyebrow text-safety">Estimated cost</p>
                    <p className="mt-1 font-display text-3xl md:text-4xl leading-none">
                      {AUD(range.min)} <span className="text-safety">–</span> {AUD(range.max)}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 divide-x divide-border">
                    <div className="p-4">
                      <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Basic service</p>
                      <p className="mt-1 font-display text-xl" data-testid="estimate-min">{AUD(range.min)}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{numeric} {selected.unit} × {AUD(selected.min)}/{selected.unit}</p>
                    </div>
                    <div className="p-4">
                      <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Premium service</p>
                      <p className="mt-1 font-display text-xl" data-testid="estimate-max">{AUD(range.max)}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{numeric} {selected.unit} × {AUD(selected.max)}/{selected.unit}</p>
                    </div>
                  </div>
                  {selected.note && (
                    <div className="p-4 border-t border-border bg-concrete text-xs text-muted-foreground flex gap-2">
                      <Info size={14} className="text-safety mt-0.5 shrink-0" />
                      <span>{selected.note}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground mt-2">Enter a size above to see the indicative range.</p>
              )}

              <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed border-l-2 border-safety pl-3">
                <strong className="text-asphalt">Indicative estimate only</strong> — based on average Melbourne market rates. Actual pricing depends on site access, condition, and waste type. Request a written quote for final pricing.
              </p>
            </div>
          )}
        </>
      )}

      {kind === "skip_bin" && (
        <>
          <div>
            <span className="rs-label">Skip bin size</span>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {skipBins.sizes.map((s) => {
                const active = s.code === skip_bin_size;
                return (
                  <button
                    key={s.code}
                    type="button"
                    onClick={() => setSkipSize(s.code)}
                    data-testid={`estimate-skipbin-${s.code}`}
                    className={`text-left p-3 border transition-colors flex items-start gap-3 ${
                      active ? "border-safety bg-safety/5" : "border-border bg-white hover:border-asphalt"
                    }`}
                  >
                    <span className={`w-9 h-9 grid place-items-center shrink-0 ${active ? "bg-safety text-white" : "bg-muted text-asphalt"}`}>
                      <Container size={16} />
                    </span>
                    <span className="text-sm">
                      <span className="font-display uppercase text-[13px] leading-tight block">{s.label}</span>
                      <span className="text-[11px] text-muted-foreground block mt-0.5">
                        {s.capacity} · {AUD(s.min)}–{AUD(s.max)}
                      </span>
                      <span className="text-[11px] text-muted-foreground block mt-1 italic">{s.best_for}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {selectedSize && (
            <div>
              <span className="rs-label">Waste type</span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {skipBins.waste_types.map((w) => {
                  const active = w.code === (skip_bin_waste || "general");
                  return (
                    <button
                      key={w.code}
                      type="button"
                      onClick={() => setSkipWaste(w.code)}
                      data-testid={`estimate-skipbin-waste-${w.code}`}
                      className={`text-left p-3 border transition-colors ${
                        active ? "border-safety bg-safety/5" : "border-border bg-white hover:border-asphalt"
                      }`}
                    >
                      <span className="font-display uppercase text-[13px] leading-tight block">{w.label}</span>
                      <span className="text-[11px] text-muted-foreground block mt-1">{w.note}</span>
                    </button>
                  );
                })}
              </div>

              <div className="mt-4 border border-border bg-white" data-testid="estimate-skipbin-result">
                <div className="p-4 bg-asphalt text-white">
                  <p className="rs-eyebrow text-safety">Skip bin range (GST incl.)</p>
                  <p className="mt-1 font-display text-3xl md:text-4xl leading-none" data-testid="estimate-skipbin-total">
                    {AUD(skipMin)} <span className="text-safety">–</span> {AUD(skipMax)}
                  </p>
                  <p className="text-xs text-white/70 mt-2">
                    {selectedSize.label} · {selectedWaste?.label} · {skipBins.hire_days}-day hire
                  </p>
                </div>
                <div className="grid grid-cols-2 divide-x divide-border">
                  <div className="p-4">
                    <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Standard</p>
                    <p className="mt-1 font-display text-xl">{AUD(skipMin)}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Kerbside drop-off, easy access</p>
                  </div>
                  <div className="p-4">
                    <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Premium</p>
                    <p className="mt-1 font-display text-xl">{AUD(skipMax)}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Difficult access / permits / heavy loads</p>
                  </div>
                </div>
                <div className="p-4 border-t border-border bg-concrete text-xs text-muted-foreground space-y-1">
                  {skipBins.notes.map((n, i) => (
                    <div key={i} className="flex gap-2">
                      <Info size={12} className="text-safety mt-0.5 shrink-0" />
                      <span>{n}</span>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Info size={12} className="text-safety mt-0.5 shrink-0" />
                    <span>Extra days: {AUD(skipBins.extra_day_fee_min)}–{AUD(skipBins.extra_day_fee_max)}/day beyond {skipBins.hire_days} days.</span>
                  </div>
                </div>
              </div>

              <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed border-l-2 border-safety pl-3">
                <strong className="text-asphalt">Indicative range only</strong> — final quote confirmed by matched local operator based on access and drop-off suburb.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
